<template>
  <!-- Main auth modal. While the forgot-password sheet is stacked on top,
       the main modal must not react to Escape/outside-click/Android-back —
       only the top sheet closes (hence :dismissible). -->
  <AppModal
    :model-value="showModal"
    size="sm"
    :mobile-sheet="false"
    :dismissible="!showForgotPassword"
    :aria-label="mode === 'login' ? t('auth.sign_in') : t('auth.create_account')"
    initial-focus="first-input"
    @close="closeModal"
  >
    <template #header>
      <div class="flex items-center justify-between px-6 py-4 shrink-0" style="border-bottom: 1px solid var(--border-subtle);">
        <h2 id="auth-modal-title" class="m-0" style="font-size: var(--text-xl); color: var(--text-primary);">{{ mode === 'login' ? t('auth.sign_in') : t('auth.create_account') }}</h2>
        <AppButton icon-only :aria-label="t('common.close')" class="text-2xl" @click="closeModal">×</AppButton>
      </div>
    </template>

    <!-- Email/Password Form -->
    <form @submit.prevent="handleSubmit" class="mb-4">
      <div v-if="mode === 'register'" class="mb-3">
        <AppInput
          v-model="form.displayName"
          type="text"
          name="name"
          autocomplete="name"
          :placeholder="t('auth.full_name')"
          :aria-label="t('auth.full_name')"
          :disabled="isLoading"
        />
      </div>

      <div class="mb-3">
        <AppInput
          v-model="form.email"
          type="email"
          name="email"
          autocomplete="email"
          inputmode="email"
          :placeholder="t('auth.email')"
          :aria-label="t('auth.email')"
          required
          :disabled="isLoading"
        />
      </div>

      <div class="mb-3">
        <AppInput
          v-model="form.password"
          type="password"
          name="password"
          :autocomplete="mode === 'login' ? 'current-password' : 'new-password'"
          :placeholder="t('auth.password')"
          :aria-label="t('auth.password')"
          required
          :disabled="isLoading"
          :minlength="6"
        />
      </div>

      <div v-if="confirmEmailMessage" class="p-2 mb-3" role="status" style="background: var(--steel-100); color: var(--steel-700); border-radius: 3px; font-size: var(--text-xs); border: 1px solid var(--steel-200);">
        {{ confirmEmailMessage }}
      </div>

      <div v-if="error" class="p-2 mb-3" role="alert" style="background: var(--ruby-100); color: var(--ruby-700); border-radius: 3px; font-size: var(--text-xs); border: 1px solid var(--ruby-200);">
        {{ error }}
      </div>

      <AppButton type="submit" block :loading="isLoading" :disabled="!isFormValid">
        {{ mode === 'login' ? t('auth.sign_in') : t('auth.create_account') }}
      </AppButton>
    </form>

    <!-- Mode Switch & Forgot Password -->
    <div class="text-center mb-4" style="font-size: var(--text-xs); color: var(--text-tertiary);">
      <span v-if="mode === 'login'">
        <template v-if="REGISTRATION_ENABLED">
          <AppButton variant="link" @click="switchMode">{{ t('auth.create_account_link') }}</AppButton>
          <span class="mx-2">&middot;</span>
        </template>
        <AppButton variant="link" @click="showForgotPassword = true">{{ t('auth.forgot_password') }}</AppButton>
      </span>
      <span v-else>
        {{ t('auth.have_account') }} <AppButton variant="link" @click="switchMode">{{ t('auth.sign_in_link') }}</AppButton>
      </span>
    </div>

    <AppDivider :label="t('common.or')" />

    <!-- Social Login Buttons.
         Google is the only provider enabled on the Identity Platform config
         (verified 2026-07-30: `defaultSupportedIdpConfigs` lists google.com and
         nothing else). An Apple button used to sit beside it and could only
         ever fail — the sign-in layer still has `signInWithApple`, so restore
         the button here when Apple is actually turned on, which iOS will need
         anyway (App Store guideline 4.8 requires it alongside other socials). -->
    <div class="flex gap-2">
      <AppButton variant="outline" block :disabled="isLoading" class="flex-1" @click="handleGoogleSignIn">
        <svg class="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Google
      </AppButton>
    </div>

    <!-- With sign-ups off, the social buttons above still work for existing
         accounts — say up front that they won't create a new one, instead of
         letting a first-time visitor find out via a server error. -->
    <p
      v-if="!REGISTRATION_ENABLED"
      class="text-center"
      style="font-size: var(--text-xs); color: var(--text-tertiary); margin-top: 12px;"
    >
      {{ t('auth.registration_disabled_note') }}
    </p>
  </AppModal>

  <!-- Forgot Password Modal — mounted after the main modal so it stacks above
       it at the same z-modal token. -->
  <AppModal
    v-model="showForgotPassword"
    size="sm"
    :mobile-sheet="false"
    :aria-label="t('auth.reset_password')"
    initial-focus="first-input"
    @close="closeForgotPassword"
  >
    <template #header>
      <div class="flex items-center justify-between px-6 py-4 shrink-0" style="border-bottom: 1px solid var(--border-subtle);">
        <h2 id="forgot-password-title" class="m-0" style="font-size: var(--text-xl); color: var(--text-primary);">{{ t('auth.reset_password') }}</h2>
        <AppButton icon-only :aria-label="t('common.close')" class="text-2xl" @click="closeForgotPassword">×</AppButton>
      </div>
    </template>

    <form @submit.prevent="handleForgotPassword">
      <div class="mb-3">
        <AppInput
          v-model="forgotEmail"
          type="email"
          name="email"
          autocomplete="email"
          inputmode="email"
          :placeholder="t('auth.email')"
          :aria-label="t('auth.email')"
          required
          :disabled="isLoading"
        />
      </div>

      <div v-if="resetMessage" class="p-2 mb-3" role="status" style="background: var(--steel-100); color: var(--steel-700); border-radius: 3px; font-size: var(--text-xs); border: 1px solid var(--steel-200);">
        {{ resetMessage }}
      </div>

      <div v-if="error" class="p-2 mb-3" role="alert" style="background: var(--ruby-100); color: var(--ruby-700); border-radius: 3px; font-size: var(--text-xs); border: 1px solid var(--ruby-200);">
        {{ error }}
      </div>

      <AppButton type="submit" block :loading="isLoading">
        {{ t('auth.send_reset_link') }}
      </AppButton>
    </form>
  </AppModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { clearError, login, register, resetPassword, signInWithGoogle } from "../stores/authStore"
import { localizeAuthError } from "../utils/authErrors"
import { AppButton, AppDivider, AppInput, AppModal } from "./ui"

interface Props {
  showModal: boolean
  initialMode?: "login" | "register"
}

interface Emits {
  (e: "close"): void
  (e: "success"): void
}

const props = withDefaults(defineProps<Props>(), {
  initialMode: "login",
})

const emit = defineEmits<Emits>()

const { t } = useI18n()

// Registration is disabled by default (this is a personal-use deployment).
// Set VITE_ALLOW_REGISTRATION=true to re-enable the sign-up UI. NOTE: this only
// hides the UI — the authoritative control is Supabase Auth's "Allow new users
// to sign up" toggle, which also blocks OAuth/anonymous and direct API sign-ups.
const REGISTRATION_ENABLED = import.meta.env.VITE_ALLOW_REGISTRATION === "true"

const mode = ref<"login" | "register">(REGISTRATION_ENABLED ? props.initialMode : "login")

// Watch for changes in initialMode prop
watch(
  () => props.initialMode,
  (newMode) => {
    mode.value = REGISTRATION_ENABLED ? newMode : "login"
  },
  { immediate: true },
)
const isLoading = ref(false)
const error = ref<string | null>(null)
const confirmEmailMessage = ref("")
const showForgotPassword = ref(false)
const forgotEmail = ref("")
const resetMessage = ref("")

const form = ref({
  email: "",
  password: "",
  displayName: "",
})

const isFormValid = computed(() => {
  const emailValid = form.value.email.includes("@")
  const passwordValid = form.value.password.length >= 6
  const nameValid = mode.value === "login" || form.value.displayName.trim().length > 0

  return emailValid && passwordValid && nameValid
})

function closeModal() {
  clearError()
  error.value = null
  confirmEmailMessage.value = ""
  emit("close")
}

function closeForgotPassword() {
  showForgotPassword.value = false
  resetMessage.value = ""
  error.value = null
}

function switchMode() {
  if (!REGISTRATION_ENABLED) return
  mode.value = mode.value === "login" ? "register" : "login"
  error.value = null
  clearError()
}

async function handleSubmit() {
  if (!isFormValid.value) return

  isLoading.value = true
  error.value = null
  confirmEmailMessage.value = ""

  try {
    if (mode.value === "login") {
      await login({
        email: form.value.email,
        password: form.value.password,
      })
    } else {
      const confirmationRequired = await register({
        email: form.value.email,
        password: form.value.password,
        displayName: form.value.displayName,
      })
      if (confirmationRequired) {
        // Supabase "Confirm email" is on: the account exists but there is no
        // session yet. Keep the modal open on the login form with a notice
        // instead of pretending the user is signed in.
        confirmEmailMessage.value = t("auth.confirm_email_sent", { email: form.value.email })
        mode.value = "login"
        form.value.password = ""
        return
      }
    }

    emit("success")
    closeModal()
  } catch (err: any) {
    error.value = localizeAuthError(err, t)
  } finally {
    isLoading.value = false
  }
}

async function handleGoogleSignIn() {
  isLoading.value = true
  error.value = null

  try {
    await signInWithGoogle()
    emit("success")
    closeModal()
  } catch (err: any) {
    error.value = localizeAuthError(err, t)
  } finally {
    isLoading.value = false
  }
}

async function handleForgotPassword() {
  if (!forgotEmail.value) return

  isLoading.value = true
  error.value = null
  resetMessage.value = ""

  try {
    await resetPassword(forgotEmail.value)
    resetMessage.value = t("auth.reset_link_sent")
  } catch (err: any) {
    error.value = localizeAuthError(err, t)
  } finally {
    isLoading.value = false
  }
}
</script>
